"use client";

import { HintText } from "@admin-ui/components/base/input/hint-text";
import { Label } from "@admin-ui/components/base/input/label";
import { cx } from "@admin-ui/utils/cx";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import type { ReactNode } from "react";
import { Toolbar } from "./toolbar";

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  label?: string;
  placeholder?: string;
  hint?: ReactNode;
  isInvalid?: boolean;
  className?: string;
}

const proseClasses = cx(
  "prose prose-sm max-w-none px-3 py-2 text-primary outline-hidden",
  "[&_.tiptap]:min-h-32 [&_.tiptap]:outline-hidden",
  "[&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none",
  "[&_.tiptap_p.is-editor-empty:first-child::before]:float-left",
  "[&_.tiptap_p.is-editor-empty:first-child::before]:h-0",
  "[&_.tiptap_p.is-editor-empty:first-child::before]:text-fg-quaternary",
  "[&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
);

export function RichTextEditor({
  value,
  onChange,
  label,
  placeholder,
  hint,
  isInvalid,
  className,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
      }),
      Underline,
    ],
    content: value,
    immediatelyRender: false,
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
    editorProps: {
      attributes: {
        ...(placeholder ? { "data-placeholder": placeholder } : {}),
        class: "min-h-32 outline-hidden",
      },
    },
  });

  return (
    <div className={cx("flex flex-col gap-1.5", className)}>
      {label && <Label>{label}</Label>}
      <div
        className={cx(
          "flex flex-col overflow-hidden rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden ring-inset",
          "focus-within:ring-2 focus-within:ring-brand",
          isInvalid && "ring-error",
        )}
      >
        {editor && <Toolbar editor={editor} />}
        <EditorContent editor={editor} className={proseClasses} />
      </div>
      {hint && <HintText>{hint}</HintText>}
    </div>
  );
}
